<template>
  <section class="hrworks">
    <div class="header">HR Works Integration</div>
    <div class="toolbar">
      <ButtonC class="hrworks-button" type="rectangle" icon="fa-refresh" label="Integrationen neu laden" :disabled="loading || busy" @click.prevent="!(loading || busy) && (loadIntegrations())" />
      <ButtonC class="hrworks-button" type="rectangle" icon="fa-plus" label="Integration anlegen" :disabled="loading || busy" @click.prevent="!(loading || busy) && (showCreate ? cancelCreate() : openCreate())" />
    </div>
    <p v-if="loading"><i class="fas fa-spinner fa-spin" /> Integrationen werden geladen…</p>
    <p v-if="error" role="alert" class="error">{{ error }}</p>
    <p v-if="notice" role="status">{{ notice }}</p>
    <form v-if="showCreate" class="card" @submit.prevent="saveCredentials()">
      <label>Integration ID <input :value="newCredentials.integrationId" readonly /></label>
      <label>Access Key <input v-model="newCredentials.accessKey" type="text" autocomplete="off" required :disabled="creating" /></label>
      <label>Secret Access Key <input v-model="newCredentials.secretAccessKey" type="password" autocomplete="new-password" required :disabled="creating" /></label>
      <ButtonC class="hrworks-button" type="rectangle green" icon="fa-save" :label="creating ? 'Wird angelegt…' : 'Anlegen'" :disabled="creating || loading || busy" @click.prevent="!(creating || loading || busy) && (submitForm($event, 'credentials'))" />
      <ButtonC class="hrworks-button" type="rectangle" icon="fa-times" label="Abbrechen" :disabled="creating" @click.prevent="!(creating) && (cancelCreate())" />
    </form>
    <p v-if="!loading && !error && !integrations.length">Keine Integrationen vorhanden.</p>
    <p v-if="integrations.length">Die Authentifizierung dauert ca. 10 Sekunden je Integration.</p>
    <article v-for="integration in integrations" :key="integration.integrationId" class="card">
      <strong v-if="integration.active === false">{{ integration.integrationId }} - Inaktiv</strong>
      <template v-else>
        <strong>{{ integration.integrationId }}</strong>
        <p aria-live="polite">
          <i v-if="integration.authStatus === 'authenticating'" class="fas fa-spinner fa-spin" />
          {{ authLabels[integration.authStatus] }}
        </p>
        <p v-if="integration.error" role="alert" class="error">{{ integration.error }}</p>
        <div class="credential-list">
          <strong>Zugangsschlüssel</strong>
          <p v-if="hasRetiringCredentials(integration)" role="alert" class="credential-warning">
            <i class="fas fa-exclamation-triangle" /> Es sind auslaufende Zugangsschlüssel vorhanden. Bitte mit „Vorherige Schlüssel entfernen“ stilllegen.
          </p>
          <p v-if="!visibleCredentials(integration).length">Keine gültigen Zugangsschlüssel vorhanden.</p>
          <div v-else class="table-container">
            <table class="dead-letter-table">
              <thead><tr><th scope="col">Version</th><th scope="col">Access Key</th><th scope="col">Status</th><th scope="col">Secret Access Key</th></tr></thead>
              <tbody>
                <tr v-for="(credential, index) in visibleCredentials(integration)" :key="credential.credentialVersion || credential.id || index">
                  <td>{{ displayValue(credential.credentialVersion) }}</td>
                  <td>{{ displayValue(credential.accessKey) }}</td>
                  <td>{{ credentialStatusLabels[normalizeCredentialStatus(credential.status)] || displayValue(credential.status) }}</td>
                  <td aria-label="Secret Access Key verborgen">••••••••</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="toolbar">
          <ButtonC class="hrworks-button" type="rectangle" icon="fa-key" label="Authentifizieren" :disabled="integration.busy || loading" @click.prevent="!(integration.busy || loading) && (authenticate(integration))" />
          <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Zugangsschlüssel rotieren" :disabled="integration.busy || loading" @click.prevent="!(integration.busy || loading) && (integration.showCredentials ? cancelRotation(integration) : integration.showCredentials = true)" />
          <ButtonC class="hrworks-button" type="rectangle" icon="fa-cog" :label="action.label" v-for="action in credentialActions" :key="action.path"
                  :disabled="integration.busy || loading" @click.prevent="!(integration.busy || loading) && (changeIntegration(integration, action))" />
        </div>
        <p v-if="integration.busy && integration.authStatus !== 'authenticating'" role="status">Aktion wird ausgeführt…</p>
        <form v-if="integration.showCredentials" @submit.prevent="saveCredentials(integration)">
          <label>Neuer Access Key <input v-model="integration.accessKey" type="text" autocomplete="off" required :disabled="integration.busy" /></label>
          <label>Neuer Secret Access Key <input v-model="integration.secretAccessKey" type="password" autocomplete="new-password" required :disabled="integration.busy" /></label>
          <ButtonC class="hrworks-button" type="rectangle green" icon="fa-save" label="Rotation speichern" :disabled="integration.busy || loading" @click.prevent="!(integration.busy || loading) && (submitForm($event, 'credentials', integration))" />
          <ButtonC class="hrworks-button" type="rectangle" icon="fa-times" label="Abbrechen" :disabled="integration.busy" @click.prevent="!(integration.busy) && (cancelRotation(integration))" />
        </form>
        <div class="toolbar settings-toggle">
          <ButtonC class="hrworks-button" type="rectangle" :icon="integration.showSettings ? 'fa-chevron-up' : 'fa-chevron-down'" :label="integration.showSettings ? 'Einstellungen schließen' : 'Einstellungen öffnen'" :disabled="integration.settingsLoading" @click.prevent="toggleIntegrationPanel(integration, 'settings')" />
          <ButtonC class="hrworks-button" type="rectangle" :icon="integration.showImportSettings ? 'fa-chevron-up' : 'fa-chevron-down'" :label="integration.showImportSettings ? 'Import-Einstellungen schließen' : 'Import-Einstellungen öffnen'" :disabled="integration.importSettingsLoading" @click.prevent="toggleIntegrationPanel(integration, 'import')" />
        </div>
        <p v-if="integration.settingsLoaded && integration.settingsEmpty && !integration.showSettings" class="credential-warning" role="alert">Für diese Integration sind noch keine Einstellungen hinterlegt.</p>
        <section v-if="integration.showSettings" class="integration-settings">
          <div class="settings-panel-heading">
            <h4>Integrationseinstellungen</h4>
            <ButtonC class="hrworks-button" type="rectangle green" icon="fa-save" :label="integration.settingsSaving ? 'Wird gespeichert…' : 'Einstellungen speichern'" :disabled="!integration.settingsLoaded || integration.settingsSaving || !settingsChanged(integration)" @click.prevent="settingsChanged(integration) && saveIntegrationSettings(integration)" />
          </div>
          <p v-if="integration.settingsLoading" role="status"><i class="fas fa-spinner fa-spin" /> Einstellungen und Typen werden geladen…</p>
          <p v-if="integration.settingsError" class="error" role="alert">{{ integration.settingsError }}</p>
          <p v-if="integration.settingsLoaded && integration.settingsEmpty" class="credential-warning" role="alert">Für diese Integration sind noch keine Einstellungen hinterlegt.</p>
          <template v-if="integration.settingsLoaded">
            <div class="settings-layout">
            <div class="settings-group">
              <div class="settings-heading">
                <h5>/persons</h5>
                <ButtonC class="hrworks-button" type="rectangle" :icon="integration.personsImporting ? 'fa-spinner fa-spin' : 'fa-download'" label="Jetzt importieren" :disabled="integration.personsImporting || integration.busy || integration.settingsSaving || employeeSyncLoading || employeeSyncSaving" @click.prevent="importPersons(integration)" />
              </div>
              <label class="setting-option"><input type="checkbox" v-model="integration.settings.persons.onlyActive" :disabled="integration.settingsSaving" /> Nur aktive Mitarbeiter</label>
              <p v-if="employeeSyncLoading" role="status">Mitarbeiter-Sync-Einstellungen werden geladen…</p>
              <template v-if="employeeSyncData">
                <label class="employee-sync-option">
                  <span>HR Works {{ $t('settings.sync.employeeDataChangeMode') }}</span>
                  <select :value="employeeSyncData.employeeChangeMode" :disabled="employeeSyncLoading || employeeSyncSaving" @change="updateEmployeeSyncData('employeeChangeMode', Number($event.target.value))">
                    <option :value="0">{{ $t('settings.sync.employeeChangesConfirm') }}</option>
                    <option :value="1">{{ $t('settings.sync.employeeChangesApplyImmediately') }}</option>
                  </select>
                </label>
                <label class="employee-sync-option">
                  <input type="checkbox" :checked="employeeSyncData.employeeAutoCreate" :disabled="employeeSyncLoading || employeeSyncSaving" @change="updateEmployeeSyncData('employeeAutoCreate', $event.target.checked)" />
                  <span>Neue Mitarbeiter automatisch anlegen</span>
                </label>
              </template>
              <p v-if="employeeSyncSaving" role="status">Mitarbeiter-Sync-Einstellungen werden gespeichert…</p>
              <p v-if="employeeSyncError" class="error" role="alert">{{ employeeSyncError }}</p>
              <p v-if="integration.personsImporting" role="status">Personenimport wird gestartet…</p>
              <p v-if="integration.personsImportNotice" role="status">{{ integration.personsImportNotice }}</p>
              <p v-if="integration.personsImportError" class="error" role="alert">{{ integration.personsImportError }}</p>
            </div>
            <div class="settings-group">
              <div class="settings-heading">
                <h5>/absences</h5>
                <ButtonC v-if="!integration.absenceImport.open" class="hrworks-button" type="rectangle" icon="fa-download" label="Jetzt importieren" :disabled="integration.busy || integration.settingsSaving" @click.prevent="openDateRangeImport(integration, 'absences')" />
              </div>
              <div v-if="integration.absenceImport.open" class="dated-import-panel">
              <strong>Importzeitraum</strong>
              <div class="import-date-range">
                <label>Von <input type="date" :value="integration.absenceImport.beginDate" :disabled="integration.absenceImport.importing" @change="updateImportDate(integration.absenceImport, 'beginDate', $event.target.value)" /></label>
                <label>Bis <input type="date" :value="integration.absenceImport.endDate" :disabled="integration.absenceImport.importing" @change="updateImportDate(integration.absenceImport, 'endDate', $event.target.value)" /></label>
              </div>
              <div class="toolbar">
                <ButtonC class="hrworks-button" type="rectangle green" :icon="integration.absenceImport.importing ? 'fa-spinner fa-spin' : 'fa-download'" label="Import starten" :disabled="integration.absenceImport.importing || integration.busy || integration.settingsSaving || !validImportDateRange(integration.absenceImport)" @click.prevent="importDateRange(integration, 'absences')" />
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-times" label="Abbrechen" :disabled="integration.absenceImport.importing" @click.prevent="closeDateRangeImport(integration.absenceImport)" />
              </div>
              </div>
              <p v-if="integration.absenceImport.importing" role="status">Abwesenheitsimport wird gestartet…</p>
              <p v-if="integration.absenceImport.notice" role="status">{{ integration.absenceImport.notice }}</p>
              <p v-if="integration.absenceImport.error" class="error" role="alert">{{ integration.absenceImport.error }}</p>
              <label class="setting-option"><input type="checkbox" v-model="integration.settings.absences.onlyActive" :disabled="integration.settingsSaving" /> Nur aktive Mitarbeiter</label>
              <div class="settings-heading"><strong>Statusfilter</strong>
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-check-square" :label="allSettingsSelected(integration, 'absences', 'statusFilter', absenceStatuses) ? 'Alle abwählen' : 'Alle markieren'" :disabled="integration.settingsSaving" @click.prevent="toggleAllSettings(integration, 'absences', 'statusFilter', absenceStatuses)" />
              </div>
              <label v-for="status in absenceStatuses" :key="'absence-' + status" class="setting-option"><input type="checkbox" :checked="integration.settings.absences.statusFilter.includes(status)" :disabled="integration.settingsSaving" @change="toggleSettingValue(integration, 'absences', 'statusFilter', status, $event.target.checked)" /> {{ status }}</label>
              <div class="settings-heading"><strong>Abwesenheitstypen</strong>
                <div class="inline-actions">
                  <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Aktualisieren" :disabled="integration.refreshingAbsenceTypes || integration.settingsSaving" @click.prevent="!integration.refreshingAbsenceTypes && refreshTypes(integration, 'absence')" />
                  <ButtonC class="hrworks-button" type="rectangle" icon="fa-check-square" :label="allSettingsSelected(integration, 'absences', 'types', integration.absenceTypes.map(type => type.key)) ? 'Alle abwählen' : 'Alle markieren'" :disabled="integration.settingsSaving || integration.absenceTypesLoading || !integration.absenceTypes.length" @click.prevent="toggleAllSettings(integration, 'absences', 'types', integration.absenceTypes.map(type => type.key))" /></div>
              </div>
              <p v-if="integration.absenceTypesLoading" role="status">Abwesenheitstypen werden geladen…</p>
              <p v-else-if="integration.absenceTypes.length === 0" class="credential-warning" role="alert">Keine Abwesenheitstypen hinterlegt. Bitte aktualisieren.</p>
              <label v-for="type in integration.absenceTypes" :key="'absence-type-' + type.key" class="setting-option type-mapping-option">
                <input type="checkbox" :checked="integration.settings.absences.types.includes(type.key)" :disabled="integration.settingsSaving" @change="toggleSettingValue(integration, 'absences', 'types', type.key, $event.target.checked)" />
                <span class="type-option-name"><strong>{{ type.key }}</strong> — {{ type.name }}</span>
                <select :value="typeMappingSelection(integration.absenceTypeMappings, type.key)" @change="upsertAvailabilityTypeMapping(integration, 'absence', type, $event.target.value)" :class="['availability-type-select', { 'availability-type-unmapped': !typeMappingSelection(integration.absenceTypeMappings, type.key) }]" :disabled="integration.settingsSaving || integration.savingAbsenceTypeMappings" :aria-label="'HR Works Abwesenheitstyp ' + type.key + ' zuordnen'">
                  <option value="">⚠ Kein Verfügbarkeitstyp zugeordnet</option>
                  <optgroup v-for="availabilityType in availabilityTypeGroups" :key="'absence-availability-' + availabilityType.value" :label="availabilityType.label">
                    <option :value="'type:' + availabilityType.value">{{ availabilityType.label }}</option>
                    <option v-for="subType in availabilityType.subTypes" :key="'absence-subtype-' + subType.value" :value="'subtype:' + subType.value">↳ {{ subType.label }}</option>
                  </optgroup>
                </select>
              </label>
              <p v-if="integration.absenceTypeMappingError" class="error" role="alert">{{ integration.absenceTypeMappingError }}</p>
              <p v-if="integration.absenceTypesError" class="error" role="alert">{{ integration.absenceTypesError }}</p>
            </div>
            <div class="settings-group">
              <div class="settings-heading">
                <h5>/sick-leaves</h5>
                <ButtonC v-if="!integration.sickLeaveImport.open" class="hrworks-button" type="rectangle" icon="fa-download" label="Jetzt importieren" :disabled="integration.busy || integration.settingsSaving" @click.prevent="openDateRangeImport(integration, 'sick-leaves')" />
              </div>
              <div v-if="integration.sickLeaveImport.open" class="dated-import-panel">
              <strong>Importzeitraum</strong>
              <div class="import-date-range">
                <label>Von <input type="date" :value="integration.sickLeaveImport.beginDate" :disabled="integration.sickLeaveImport.importing" @change="updateImportDate(integration.sickLeaveImport, 'beginDate', $event.target.value)" /></label>
                <label>Bis <input type="date" :value="integration.sickLeaveImport.endDate" :disabled="integration.sickLeaveImport.importing" @change="updateImportDate(integration.sickLeaveImport, 'endDate', $event.target.value)" /></label>
              </div>
              <div class="toolbar">
                <ButtonC class="hrworks-button" type="rectangle green" :icon="integration.sickLeaveImport.importing ? 'fa-spinner fa-spin' : 'fa-download'" label="Import starten" :disabled="integration.sickLeaveImport.importing || integration.busy || integration.settingsSaving || !validImportDateRange(integration.sickLeaveImport)" @click.prevent="importDateRange(integration, 'sick-leaves')" />
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-times" label="Abbrechen" :disabled="integration.sickLeaveImport.importing" @click.prevent="closeDateRangeImport(integration.sickLeaveImport)" />
              </div>
              </div>
              <p v-if="integration.sickLeaveImport.importing" role="status">Krankenstandsimport wird gestartet…</p>
              <p v-if="integration.sickLeaveImport.notice" role="status">{{ integration.sickLeaveImport.notice }}</p>
              <p v-if="integration.sickLeaveImport.error" class="error" role="alert">{{ integration.sickLeaveImport.error }}</p>
              <label class="setting-option"><input type="checkbox" v-model="integration.settings.sickLeaves.onlyActive" :disabled="integration.settingsSaving" /> Nur aktive Mitarbeiter</label>
              <div class="settings-heading"><strong>Statusfilter</strong>
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-check-square" :label="allSettingsSelected(integration, 'sickLeaves', 'statusFilter', sickLeaveStatuses) ? 'Alle abwählen' : 'Alle markieren'" :disabled="integration.settingsSaving" @click.prevent="toggleAllSettings(integration, 'sickLeaves', 'statusFilter', sickLeaveStatuses)" />
              </div>
              <label v-for="status in sickLeaveStatuses" :key="'sick-leave-' + status" class="setting-option"><input type="checkbox" :checked="integration.settings.sickLeaves.statusFilter.includes(status)" :disabled="integration.settingsSaving" @change="toggleSettingValue(integration, 'sickLeaves', 'statusFilter', status, $event.target.checked)" /> {{ status }}</label>
              <div class="settings-heading"><strong>Krankenstandstypen</strong>
                <div class="inline-actions">
                  <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Aktualisieren" :disabled="integration.refreshingSickLeaveTypes || integration.settingsSaving" @click.prevent="!integration.refreshingSickLeaveTypes && refreshTypes(integration, 'sick-leave')" />
                  <ButtonC class="hrworks-button" type="rectangle" icon="fa-check-square" :label="allSettingsSelected(integration, 'sickLeaves', 'types', integration.sickLeaveTypes.map(type => type.key)) ? 'Alle abwählen' : 'Alle markieren'" :disabled="integration.settingsSaving || integration.sickLeaveTypesLoading || !integration.sickLeaveTypes.length" @click.prevent="toggleAllSettings(integration, 'sickLeaves', 'types', integration.sickLeaveTypes.map(type => type.key))" /></div>
              </div>
              <p v-if="integration.sickLeaveTypesLoading" role="status">Krankenstandstypen werden geladen…</p>
              <p v-else-if="integration.sickLeaveTypes.length === 0" class="credential-warning" role="alert">Keine Krankenstandstypen hinterlegt. Bitte aktualisieren.</p>
              <label v-for="type in integration.sickLeaveTypes" :key="'sick-leave-type-' + type.key" class="setting-option type-mapping-option">
                <input type="checkbox" :checked="integration.settings.sickLeaves.types.includes(type.key)" :disabled="integration.settingsSaving" @change="toggleSettingValue(integration, 'sickLeaves', 'types', type.key, $event.target.checked)" />
                <span class="type-option-name"><strong>{{ type.key }}</strong> — {{ type.name }}</span>
                <select :value="typeMappingSelection(integration.sickLeaveTypeMappings, type.key)" @change="upsertAvailabilityTypeMapping(integration, 'sick-leave', type, $event.target.value)" :class="['availability-type-select', { 'availability-type-unmapped': !typeMappingSelection(integration.sickLeaveTypeMappings, type.key) }]" :disabled="integration.settingsSaving || integration.savingSickLeaveTypeMappings" :aria-label="'HR Works Krankenstandstyp ' + type.key + ' zuordnen'">
                  <option value="">⚠ Kein Verfügbarkeitstyp zugeordnet</option>
                  <optgroup v-for="availabilityType in availabilityTypeGroups" :key="'sick-availability-' + availabilityType.value" :label="availabilityType.label">
                    <option :value="'type:' + availabilityType.value">{{ availabilityType.label }}</option>
                    <option v-for="subType in availabilityType.subTypes" :key="'sick-subtype-' + subType.value" :value="'subtype:' + subType.value">↳ {{ subType.label }}</option>
                  </optgroup>
                </select>
              </label>
              <p v-if="integration.sickLeaveTypeMappingError" class="error" role="alert">{{ integration.sickLeaveTypeMappingError }}</p>
              <p v-if="integration.sickLeaveTypesError" class="error" role="alert">{{ integration.sickLeaveTypesError }}</p>
            </div>
            </div>
            <p v-if="integration.settingsSaveError" class="error" role="alert">{{ integration.settingsSaveError }}</p>
            <p v-if="integration.settingsSaveNotice" role="status">{{ integration.settingsSaveNotice }}</p>
          </template>
        </section>
        <section v-if="integration.showImportSettings" class="integration-settings">
          <h4>Import-Einstellungen</h4>
          <p v-if="integration.importSettingsLoading" role="status"><i class="fas fa-spinner fa-spin" /> Importdaten werden geladen…</p>
          <p v-if="integration.importSettingsError" class="error" role="alert">{{ integration.importSettingsError }}</p>
          <p v-if="integration.personImportSettingsError && !integration.personImportSettingsLoaded" class="error" role="alert">{{ integration.personImportSettingsError }}</p>
          <label v-if="integration.personImportSettingsLoaded" class="import-row default-contract-setting">
            <span>Standard-Kollektivvertrag</span>
            <select :value="integration.personImportSettings.defaultCollectiveContractMdId == null ? '' : integration.personImportSettings.defaultCollectiveContractMdId" :disabled="importControlsDisabled(integration) || collectiveContractsLoading || !!collectiveContractsError" @change="setDefaultCollectiveContract(integration, $event.target.value)">
              <option value="">Kein Standard-Kollektivvertrag</option>
              <option v-for="contract in collectiveContracts" :key="contract.id" :value="contract.id">{{ contract.name }}</option>
            </select>
          </label>
          <p v-if="collectiveContractsError" class="error" role="alert">{{ collectiveContractsError }}</p>
          <div class="import-layout">
            <div class="settings-group">
              <div class="settings-heading">
                <h5>Niederlassungen / Betriebsstätten</h5>
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Aktualisieren" :disabled="integration.importSettingsLoading || integration.refreshingPermanentEstablishments" @click.prevent="!(integration.importSettingsLoading || integration.refreshingPermanentEstablishments) && refreshImportList(integration, 'permanent-establishments')" />
              </div>
              <label v-if="integration.personImportSettingsLoaded" class="import-row setting-option"><span>Personen ohne Niederlassung / Betriebsstätte importieren</span><input type="checkbox" v-model="integration.personImportSettings.importPersonsWithoutPermanentEstablishment" :disabled="importControlsDisabled(integration)" @change="savePersonImportSettings(integration)" /></label>
              <p v-if="!integration.importSettingsLoading && integration.permanentEstablishments.length === 0" class="credential-warning">Keine Niederlassungen oder Betriebsstätten gefunden.</p>
              <label v-for="establishment in integration.permanentEstablishments" :key="'permanent-establishment-' + importRecordId(establishment, 'permanent-establishments')" class="import-row">
                <span>{{ importRecordName(establishment, 'permanent-establishments') }}</span>
                <span><input type="checkbox" :checked="Boolean(establishment.import)" :disabled="importControlsDisabled(integration)" @change="setPermanentEstablishmentImport(integration, establishment, $event.target.checked)" /> Importieren</span>
              </label>
            </div>
            <div class="settings-group">
              <div class="settings-heading">
                <h5>Zeiterfassungsarten</h5>
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Aktualisieren" :disabled="integration.importSettingsLoading || integration.refreshingTimeRecordingRegulations" @click.prevent="!(integration.importSettingsLoading || integration.refreshingTimeRecordingRegulations) && refreshImportList(integration, 'time-recording-regulations')" />
              </div>
              <label v-if="integration.personImportSettingsLoaded" class="import-row setting-option"><span>Personen ohne Zeiterfassungsart importieren</span><input type="checkbox" v-model="integration.personImportSettings.importPersonsWithoutTimeRecordingRegulation" :disabled="importControlsDisabled(integration)" @change="savePersonImportSettings(integration)" /></label>
              <p v-if="!integration.importSettingsLoading && integration.timeRecordingRegulations.length === 0" class="credential-warning">Keine Zeiterfassungsarten gefunden.</p>
              <label v-for="regulation in integration.timeRecordingRegulations" :key="'time-recording-regulation-' + regulation.id" class="import-row">
                <span><strong>{{ regulation.key }}</strong> — {{ regulation.title }}</span>
                <span><input type="checkbox" :checked="Boolean(regulation.import)" :disabled="importControlsDisabled(integration)" @change="setTimeRecordingRegulationImport(integration, regulation, $event.target.checked)" /> Importieren</span>
              </label>
            </div>
            <div class="settings-group">
              <div class="settings-heading">
                <h5>Organisationseinheiten</h5>
                <ButtonC class="hrworks-button" type="rectangle" icon="fa-sync" label="Aktualisieren" :disabled="integration.importSettingsLoading || integration.refreshingOrganizationUnits" @click.prevent="!(integration.importSettingsLoading || integration.refreshingOrganizationUnits) && refreshImportList(integration, 'organization-units')" />
              </div>
              <label v-if="integration.personImportSettingsLoaded" class="import-row">
                <span>Store für Personen ohne Organisationseinheit</span>
                <select :value="integration.personImportSettings.missingOrganizationUnitStoreId == null ? '' : integration.personImportSettings.missingOrganizationUnitStoreId" :disabled="importControlsDisabled(integration) || storesLoading" @change="setMissingOrganizationUnitStore(integration, $event.target.value)">
                  <option value="">Nicht importieren</option>
                  <option v-for="store in stores" :key="'missing-ou-store-' + store.id" :value="store.id">{{ store.name }}</option>
                </select>
              </label>
              <p v-if="!integration.importSettingsLoading && integration.organizationUnits.length === 0" class="credential-warning">Keine Organisationseinheiten gefunden.</p>
              <label v-for="row in organizationUnitRows(integration)" :key="'organization-unit-' + importRecordId(row.unit, 'organization-units')" class="import-row">
                <span :style="{ paddingLeft: (row.depth * 18) + 'px' }"><span v-if="row.depth" aria-hidden="true">↳ </span>{{ importRecordName(row.unit, 'organization-units') }}</span>
                <select :value="row.unit.storeId == null ? '' : row.unit.storeId" :disabled="importControlsDisabled(integration) || storesLoading" @change="mapOrganizationUnit(integration, row.unit, $event.target.value)">
                  <option value="">Nicht importieren</option>
                  <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.name }}</option>
                </select>
              </label>
            </div>

          </div>
          <p v-if="integration.personImportSettingsError" class="error" role="alert">{{ integration.personImportSettingsError }}</p>
          <p v-if="integration.personImportSettingsNotice" role="status">{{ integration.personImportSettingsNotice }}</p>
          <p v-if="integration.importSettingsSaving" role="status">Import-Einstellungen werden gespeichert…</p>
          <p v-if="integration.importSettingsSaveError" class="error" role="alert">{{ integration.importSettingsSaveError }}</p>
        </section>
      </template>
    </article>
    <div v-if="integrations.length > 0" class="card">
      <strong>Webhook Inbox</strong>
      <label>Integration
        <select v-model="webhookIntegrationId" :disabled="webhookLoading">
          <option value="">Alle Integrationen</option>
          <option v-for="integration in activeIntegrations" :key="integration.integrationId" :value="integration.integrationId">{{ integration.integrationId }}</option>
        </select>
      </label>
      <div class="toolbar">
        <ButtonC class="hrworks-button" type="rectangle" icon="fa-heartbeat" label="Status laden" :disabled="webhookLoading" @click.prevent="!(webhookLoading) && (loadWebhooks('status'))" />
        <ButtonC class="hrworks-button" type="rectangle" icon="fa-inbox" label="Dead Letters laden" :disabled="webhookLoading" @click.prevent="!(webhookLoading) && (loadWebhooks('dead-letters'))" />
      </div>
      <form @submit.prevent="loadWebhooks('dead-letters', deadLetterId)">
        <label>Dead Letter ID <input v-model.trim="deadLetterId" required :disabled="webhookLoading" /></label>
        <ButtonC class="hrworks-button" type="rectangle green" icon="fa-search" label="Details laden" :disabled="webhookLoading || !deadLetterId" @click.prevent="!(webhookLoading || !deadLetterId) && (submitForm($event, 'dead-letter'))" />
      </form>
      <p v-if="webhookLoading" role="status"><i class="fas fa-spinner fa-spin" /> Webhook Daten werden geladen…</p>
      <p v-if="webhookError" role="alert" class="error">{{ webhookError }}</p>
      <template v-if="webhookResult !== null">
        <p>{{ webhookResultLabel }}</p>
        <div v-if="webhookResultKind === 'status'" class="webhook-status">
          <div class="status-heading">
            <span class="health-badge" :class="webhookResult.healthy === true ? 'healthy' : 'unhealthy'">
              <i class="fas" :class="webhookResult.healthy === true ? 'fa-check-circle' : 'fa-exclamation-circle'" />
              {{ webhookResult.healthy === true ? 'Gesund' : webhookResult.healthy === false ? 'Nicht gesund' : 'Status unbekannt' }}
            </span>
            <span>Stand: {{ formatDate(webhookResult.generatedAt) }}</span>
          </div>
          <div class="metrics">
            <div v-for="metric in countMetrics" :key="metric.key" class="metric" :class="metric.tone">
              <span>{{ metric.label }}</span>
              <strong>{{ displayValue(webhookResult.counts && webhookResult.counts[metric.key]) }}</strong>
            </div>
          </div>
          <dl class="detail-grid">
            <template v-for="field in statusFields">
              <dt :key="field.key + '-label'">{{ field.label }}</dt>
              <dd :key="field.key">{{ field.date ? formatDate(webhookResult[field.key]) : displayValue(webhookResult[field.key]) }}</dd>
            </template>
          </dl>
        </div>
        <div v-else-if="webhookResultKind === 'dead-letters' && Array.isArray(webhookResult)">
          <p v-if="!webhookResult.length" class="empty-state">Keine Dead Letters vorhanden.</p>
          <div v-else class="table-container">
            <table class="dead-letter-table">
              <caption>{{ webhookResult.length }} Dead Letter{{ webhookResult.length === 1 ? '' : 's' }}</caption>
              <thead>
                <tr><th scope="col">ID / Integration</th><th scope="col">Ereignis / Ressource</th><th scope="col">Versuche</th><th scope="col">Zeitpunkte</th><th scope="col">Fehler / Payload</th><th scope="col">Aktion</th></tr>
              </thead>
              <tbody>
                <tr v-for="letter in webhookResult" :key="letter.id">
                  <td><strong>{{ letter.id }}</strong><span class="secondary">Integration: {{ displayValue(letter.integrationId) }}</span></td>
                  <td>{{ displayValue(letter.event) }}<span class="secondary">{{ displayValue(letter.action) }} · {{ displayValue(letter.resourceType) }}</span></td>
                  <td>{{ displayValue(letter.attemptCount) }}</td>
                  <td>{{ formatDate(letter.deadLetteredAt) }}<span class="secondary">Empfangen: {{ formatDate(letter.receivedAt) }}</span></td>
                  <td><span :class="{ error: letter.lastError }">{{ letter.lastError || 'Kein Fehlertext' }}</span><span class="secondary">Payload: {{ displayValue(letter.payloadAvailable) }}</span></td>
                  <td><ButtonC class="hrworks-button" type="rectangle" icon="fa-search" label="Details" :disabled="webhookLoading" @click.prevent="!(webhookLoading) && (loadWebhooks('dead-letters', letter.id, letter.integrationId))" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else-if="webhookResultKind === 'dead-letter'">
          <strong>Dead Letter Details</strong>
          <dl class="detail-grid">
            <template v-for="field in deadLetterDetails">
              <dt :key="field.key + '-label'">{{ field.label }}</dt>
              <dd :key="field.key">{{ field.date ? formatDate(field.value) : displayValue(field.value) }}</dd>
            </template>
          </dl>
          <ButtonC class="hrworks-button" type="rectangle" icon="fa-arrow-left" label="Zurück zur Liste" :disabled="webhookLoading" @click.prevent="!(webhookLoading) && (loadWebhooks('dead-letters'))" />
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import ButtonC from '../uicomponents/button.vue';
import VueEventBus from '@/vue-event-bus';

const baseUrl = '/api/sec/proxy/mltransfer/hrworks';

export default {
  name: 'HRWorksIntegration',
  components: { ButtonC },
  props: { companyId: { type: [Number, String], required: true } },
  data() {
    return {
      integrations: [], stores: [], storesLoading: false, loading: false, error: '', notice: '', generation: 0,
      collectiveContracts: [], collectiveContractsLoading: false, collectiveContractsError: '',
      employeeSyncData: null, employeeSyncLoading: false, employeeSyncSaving: false, employeeSyncError: '', employeeSyncGeneration: 0,
      showCreate: false, creating: false,
      newCredentials: { integrationId: '', accessKey: '', secretAccessKey: '' },
      webhookIntegrationId: '', deadLetterId: '', webhookLoading: false,
      webhookResult: null, webhookResultLabel: '', webhookResultKind: '', webhookError: '',
      countMetrics: [
        { key: 'total', label: 'Gesamt' },
        { key: 'processed', label: 'Verarbeitet', tone: 'positive' },
        { key: 'pending', label: 'Ausstehend' },
        { key: 'processing', label: 'In Verarbeitung' },
        { key: 'retry', label: 'Wiederholungen', tone: 'warning' },
        { key: 'deadLetter', label: 'Dead Letters', tone: 'negative' },
        { key: 'cancelled', label: 'Abgebrochen' },
        { key: 'receivedLastHour', label: 'Empfangen (letzte Stunde)' },
        { key: 'processedLastHour', label: 'Verarbeitet (letzte Stunde)' }
      ],
      statusFields: [
        { key: 'claimTimeoutSeconds', label: 'Claim Timeout (Sekunden)' },
        { key: 'claimableCount', label: 'Zur Verarbeitung verfügbar' },
        { key: 'orderingBlockedCount', label: 'Durch Reihenfolge blockiert' },
        { key: 'inactiveDrainingCount', label: 'Inaktive Integrationen im Auslauf' },
        { key: 'staleProcessingCount', label: 'Überfällige Verarbeitungen' },
        { key: 'oldestUnfinishedAt', label: 'Ältester offener Eintrag', date: true },
        { key: 'oldestDeadLetterAt', label: 'Ältester Dead Letter', date: true },
        { key: 'oldestCancelledAt', label: 'Ältester abgebrochener Eintrag', date: true }
      ],
      deadLetterFields: [
        { key: 'id', label: 'Dead Letter ID' },
        { key: 'integrationId', label: 'Integration ID' },
        { key: 'companyId', label: 'Unternehmen ML ID' },
        { key: 'jobId', label: 'Job ID' },
        { key: 'event', label: 'Ereignis' },
        { key: 'action', label: 'Aktion' },
        { key: 'resourceType', label: 'Ressourcentyp' },
        { key: 'resourceLocation', label: 'Ressource URL' },
        { key: 'resourceLocationModified', label: 'Modifizierte Ressource URL' },
        { key: 'attemptCount', label: 'Verarbeitungsversuche' },
        { key: 'receivedAt', label: 'Empfangen am', date: true },
        { key: 'deadLetteredAt', label: 'Als Dead Letter markiert am', date: true },
        { key: 'lastError', label: 'Letzter Fehler' },
        { key: 'payloadAvailable', label: 'Payload verfügbar' }
      ],
      authLabels: {
        unauthenticated: 'Nicht authentifiziert', authenticating: 'Wird authentifiziert…',
        authenticated: 'Authentifiziert', failed: 'Authentifizierung fehlgeschlagen'
      },
      absenceStatuses: ['substitutionRequested', 'cancellationRequested', 'cancelled', 'changesRequested', 'editingChanges', 'deletionRequested', 'editing', 'substitutionRejected', 'requested', 'validatedOk', 'validatedNotOk'],
      sickLeaveStatuses: ['cancellationRequested', 'cancelled', 'certificatePending', 'electronicCertificatePending', 'electronicCertificateAvailable', 'electronicCertificateRequestSent', 'electronicCertificateRequestFailed', 'reported', 'validatedOk'],
      credentialStatusLabels: { active: 'Aktiv', current: 'Aktuell', retiring: 'Auslaufend', pending_validation: 'Validierung ausstehend' },
      credentialActions: [
        { path: '/integrations/deactivate', label: 'Deaktivieren', confirm: 'Integration deaktivieren?', authenticate: false },
        { path: '/integrations/credentials/encryption-key', label: 'DB Schlüssel neu verschlüsseln', confirm: 'Zugangsdaten mit dem aktuellen DB Schlüssel neu verschlüsseln?' },
        { path: '/integrations/credentials/rollback', label: 'Schlüssel zurückrollen', confirm: 'Auf die vorherigen Zugangsschlüssel zurückrollen?', authenticate: true },
        { path: '/integrations/credentials/retire-previous', label: 'Vorherige Schlüssel entfernen', confirm: 'Vorherige Zugangsschlüssel endgültig entfernen?' }
      ]
    };
  },
  computed: {
    activeIntegrations() { return this.integrations.filter(integration => integration.active === true); },
    availabilityTypeGroups() {
      const types = this.$helpers.GetMdEmployeeAvailabilityTypes() || [];
      const subTypes = this.$helpers.GetMdEmployeeAvailabilitySubTypes() || [];
      return types.slice().sort((a, b) => Number(a.value) - Number(b.value)).map(type => ({
        ...type,
        subTypes: subTypes.filter(subType => Number(subType.parentType) === Number(type.value))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)))
      }));
    },
    busy() { return this.creating || this.integrations.some(integration => integration.busy); },
    deadLetterDetails() {
      const letter = this.webhookResult;
      if (!letter || typeof letter !== 'object' || Array.isArray(letter)) return [];
      const known = this.deadLetterFields.map(field => ({ ...field, value: letter[field.key] }));
      const extra = Object.keys(letter).filter(key => !this.deadLetterFields.some(field => field.key === key))
        .map(key => ({ key, label: key, value: letter[key] }));
      return [...known, ...extra];
    }
  },
  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.generation++;
        this.integrations = [];
        this.employeeSyncData = null;
        this.employeeSyncGeneration++;
        this.employeeSyncError = '';
        this.employeeSyncLoading = false;
        this.employeeSyncSaving = false;
        this.stores = [];
        this.loadCompanyStores();
        this.loadCollectiveContracts();
        this.cancelCreate();
        this.webhookIntegrationId = '';
        this.deadLetterId = '';
        this.webhookResult = null;
        this.webhookError = '';
        this.webhookLoading = false;
        this.creating = false;
        this.loadIntegrations();
      }
    }
  },
  beforeDestroy() { this.generation++; this.employeeSyncGeneration++; },
  methods: {
    unwrapResponse(result) {
      if (result === false || (result && (result.success === false || result.error))) throw new Error('HR Works request failed');
      if (result && Object.prototype.hasOwnProperty.call(result, 'data')) result = result.data;
      if (result === false || (result && (result.success === false || result.error))) throw new Error('HR Works request failed');
      return result;
    },
    submitForm(event, kind, integration = null) {
      const form = event.target.closest('form');
      if (!form || !form.reportValidity()) return;
      return kind === 'credentials' ? this.saveCredentials(integration) : this.loadWebhooks('dead-letters', this.deadLetterId);
    },
    async request(method, path, parameters = {}) {
      const payload = { companyId: this.companyId, ...parameters };
      const response = await axios({
        method, url: baseUrl + path,
        headers: { AUTHORIZATION: 'Bearer ' + this.$store.state.jwt },
        ...(method === 'get' ? { params: payload } : { data: payload })
      });
      // ML Transfer responses may be wrapped in data, like the other proxy calls.
      return this.unwrapResponse(response.data);
    },
    async loadIntegrations(authenticateIntegrationId = null) {
      const generation = ++this.generation;
      this.loading = true;
      this.error = '';
      this.notice = '';
      this.webhookResult = null;
      this.webhookLoading = false;
      try {
        const result = await this.request('get', '/integrations');
        if (generation !== this.generation) return;
        const list = Array.isArray(result) ? result : result && result.integrations;
        if (!Array.isArray(list) || list.some(item => !item || typeof item.integrationId !== 'string' || !item.integrationId)) {
          throw new Error('Unexpected integration response');
        }
        const previousIntegrations = this.integrations;
        this.integrations = list.map(item => {
          const previous = authenticateIntegrationId && previousIntegrations.find(integration => integration.integrationId === item.integrationId);
          return {
          integrationId: item.integrationId, active: item.active,
          credentials: Array.isArray(item.credentials) ? item.credentials : [],
          credentialStatusCounts: item.credentialStatusCounts || {},
          authStatus: previous ? previous.authStatus : 'unauthenticated', busy: false, error: previous ? previous.error : '',
          showCredentials: false, showSettings: false, showImportSettings: false, accessKey: '', secretAccessKey: '',
          importSettingsLoading: false, importSettingsSaving: false, importSettingsError: '', importSettingsSaveError: '',
          personImportSettings: this.defaultPersonImportSettings(), savedPersonImportSettings: this.defaultPersonImportSettings(), personImportSettingsLoaded: false, personImportSettingsError: '', personImportSettingsNotice: '',
          organizationUnits: [], permanentEstablishments: [], timeRecordingRegulations: [], refreshingOrganizationUnits: false, refreshingPermanentEstablishments: false, refreshingTimeRecordingRegulations: false,
          settings: this.defaultSettings(), savedSettings: this.defaultSettings(), settingsEmpty: false, settingsLoaded: false,
          personsImporting: false, personsImportNotice: '', personsImportError: '',
          absenceImport: this.defaultDatedImport(), sickLeaveImport: this.defaultDatedImport(),
          settingsLoading: false, settingsSaving: false, settingsError: '', settingsSaveError: '', settingsSaveNotice: '',
          absenceTypes: [], sickLeaveTypes: [], absenceTypeMappings: [], sickLeaveTypeMappings: [], absenceTypesLoading: false, sickLeaveTypesLoading: false,
          absenceTypesError: '', sickLeaveTypesError: '', absenceTypeMappingError: '', sickLeaveTypeMappingError: '', savingAbsenceTypeMappings: false, savingSickLeaveTypeMappings: false, refreshingAbsenceTypes: false, refreshingSickLeaveTypes: false
          };
        });
        if (!this.activeIntegrations.some(item => item.integrationId === this.webhookIntegrationId)) this.webhookIntegrationId = '';
        if (this.integrations.some(integration => integration.active !== false)) this.loadEmployeeSyncSettings();
        // Do not await these slow requests: each card tracks its own progress.
        this.integrations.filter(integration => integration.active !== false).forEach(integration => {
          if (!authenticateIntegrationId || integration.integrationId === authenticateIntegrationId) this.authenticate(integration);
          this.loadIntegrationSettings(integration);
          this.loadImportSettings(integration);
        });
      } catch (error) {
        if (generation === this.generation) this.error = 'Integrationen konnten nicht geladen werden. Bitte erneut versuchen.';
      } finally {
        if (generation === this.generation) this.loading = false;
      }
    },
    toggleIntegrationPanel(integration, panel) {
      if (panel === 'settings') {
        const open = !integration.showSettings;
        integration.showSettings = open;
        if (open) integration.showImportSettings = false;
      } else {
        const open = !integration.showImportSettings;
        integration.showImportSettings = open;
        if (open) integration.showSettings = false;
      }
    },
    async employeeSyncRequest(method, path, companyId, data = null, parameters = {}) {
      const response = await axios({
        method,
        url: '/api/sec' + path,
        headers: { AUTHORIZATION: 'Bearer ' + this.$store.state.jwt },
        params: { companyId, ...parameters },
        ...(data == null ? {} : { data })
      });
      return this.unwrapResponse(response.data);
    },
    async loadEmployeeSyncSettings() {
      if (this.employeeSyncLoading || this.employeeSyncSaving) return;
      const companyId = this.companyId;
      const generation = this.employeeSyncGeneration;
      const current = () => generation === this.employeeSyncGeneration && companyId === this.companyId;
      this.employeeSyncLoading = true;
      this.employeeSyncError = '';
      try {
        const [settings, softwares, existingData] = await Promise.all([
          this.employeeSyncRequest('get', '/employeesync/settings', companyId),
          this.employeeSyncRequest('get', '/externalsoftware', companyId),
          this.employeeSyncRequest('get', '/employeesync/data', companyId).catch(error => {
            if (error.response && error.response.status === 404) return null;
            throw error;
          })
        ]);
        if (!current()) return;
        const software = (Array.isArray(softwares) ? softwares : []).find(item => String(item.name).replace(/[^a-z0-9]/gi, '').toLowerCase() === 'hrworks');
        if (!software || !Array.isArray(settings)) throw new Error('HR Works employee sync settings unavailable');
        if (!settings.some(setting => String(setting.softwareId) === String(software.id))) {
          await this.employeeSyncRequest('post', '/employeesync/settings', companyId, {}, { softwareId: software.id });
          if (!current()) return;
          await this.employeeSyncRequest('post', '/employeesync/fields/settings/default/create', companyId, {}, { softwareId: software.id });
          if (!current()) return;
        }
        const defaults = {
          taxOfficeSerialNumber: '', enableExportViaTransferService: false, enablePeriodicExport: false,
          employeeChangeMode: 0, employeeAutoCreate: false, costCenterAutoCreate: false
        };
        let data = existingData;
        if (!data || data.id == null || data.id === -1) {
          const created = await this.employeeSyncRequest('post', '/employeesync/data', companyId, defaults);
          if (!current()) return;
          data = created && created.id != null ? { ...defaults, ...created }
            : await this.employeeSyncRequest('get', '/employeesync/data', companyId);
        }
        if (!current()) return;
        if (!data || data.id == null || data.id === -1) throw new Error('Employee sync data unavailable');
        this.employeeSyncData = { ...defaults, ...data };
        VueEventBus.$emit('EmployeeSyncSettingsEvent:updated', this.employeeSyncData);
      } catch (error) {
        if (current()) this.employeeSyncError = 'Mitarbeiter-Sync-Einstellungen konnten nicht geladen oder angelegt werden.';
      } finally {
        if (current()) this.employeeSyncLoading = false;
      }
    },
    async updateEmployeeSyncData(field, value) {
      if (!this.employeeSyncData || this.employeeSyncLoading || this.employeeSyncSaving) return;
      const companyId = this.companyId;
      const generation = this.employeeSyncGeneration;
      const current = () => generation === this.employeeSyncGeneration && companyId === this.companyId;
      const updated = { ...this.employeeSyncData, [field]: value };
      this.employeeSyncSaving = true;
      this.employeeSyncError = '';
      try {
        await this.employeeSyncRequest('put', '/employeesync/data/' + encodeURIComponent(updated.id), companyId, updated);
        if (!current()) return;
        this.employeeSyncData = updated;
        VueEventBus.$emit('EmployeeSyncSettingsEvent:updated', updated);
      } catch (error) {
        if (current()) this.employeeSyncError = 'Mitarbeiter-Sync-Einstellungen konnten nicht gespeichert werden.';
      } finally {
        if (current()) this.employeeSyncSaving = false;
      }
    },
    async loadCompanyStores() {
      this.storesLoading = true;
      try {
        const response = await this.$helpers.GetStoresSettings();
        const result = response && response.data;
        this.stores = Array.isArray(result) ? result : [];
      } catch (error) {
        this.stores = [];
      } finally {
        this.storesLoading = false;
      }
    },
    async loadCollectiveContracts() {
      const companyId = this.companyId;
      this.collectiveContracts = [];
      this.collectiveContractsLoading = true;
      this.collectiveContractsError = '';
      try {
        const response = await this.$helpers.GetCall('/api/sec/proxy/md/collectiveContracts');
        if (companyId !== this.companyId) return;
        this.collectiveContracts = response.data.map(contract => ({
          id: contract.id,
          name: contract.country === 2 ? contract.name + ' (DE)' : contract.name
        })).sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        if (companyId === this.companyId) this.collectiveContractsError = 'Kollektivverträge konnten nicht geladen werden.';
      } finally {
        if (companyId === this.companyId) this.collectiveContractsLoading = false;
      }
    },
    importRecordId(record, kind) {
      return record.id != null ? record.id : kind === 'organization-units'
        ? (record.organizationUnitId != null ? record.organizationUnitId : record.key)
        : (record.permanentEstablishmentId != null ? record.permanentEstablishmentId : record.key);
    },
    importRecordName(record, kind) {
      return record.name || record.displayName || record.label || String(this.importRecordId(record, kind));
    },
    organizationUnitRows(integration) {
      const units = integration.organizationUnits || [];
      const byId = new Map(units.map(unit => [String(this.importRecordId(unit, 'organization-units')), unit]));
      const children = new Map();
      const roots = [];
      units.forEach(unit => {
        const parentId = unit.parentOrganizationUnitId;
        if (parentId == null || parentId === '' || !byId.has(String(parentId))) {
          roots.push(unit);
        } else {
          const parentKey = String(parentId);
          if (!children.has(parentKey)) children.set(parentKey, []);
          children.get(parentKey).push(unit);
        }
      });
      const rows = [];
      const visited = new Set();
      const append = (unit, depth) => {
        const key = String(this.importRecordId(unit, 'organization-units'));
        if (visited.has(key)) return;
        visited.add(key);
        rows.push({ unit, depth });
        (children.get(key) || []).forEach(child => append(child, depth + 1));
      };
      roots.forEach(root => append(root, 0));
      // Keep malformed or cyclic records visible instead of dropping them.
      units.forEach(unit => append(unit, 0));
      return rows;
    },
    async loadImportSettings(integration) {
      integration.importSettingsLoading = true;
      integration.importSettingsError = '';
      integration.personImportSettingsError = '';
      try {
        const [units, establishments, regulations] = await Promise.all([
          this.importRequest('get', '/organization-units', integration),
          this.importRequest('get', '/permanent-establishments', integration),
          this.importRequest('get', '/time-recording-regulations', integration)
        ]);
        integration.organizationUnits = Array.isArray(units) ? units : [];
        integration.permanentEstablishments = Array.isArray(establishments) ? establishments : [];
        integration.timeRecordingRegulations = Array.isArray(regulations) ? regulations : [];
      } catch (error) {
        integration.importSettingsError = 'Import-Einstellungen konnten nicht geladen werden.';
      }
      try {
        const personSettings = await this.importRequest('get', '/import-settings', integration);
        integration.personImportSettings = {
          importPersonsWithoutTimeRecordingRegulation: Boolean(personSettings && personSettings.importPersonsWithoutTimeRecordingRegulation),
          importPersonsWithoutPermanentEstablishment: Boolean(personSettings && personSettings.importPersonsWithoutPermanentEstablishment),
          importPersonsWithoutOrganizationUnit: Boolean(personSettings && personSettings.importPersonsWithoutOrganizationUnit),
          missingOrganizationUnitStoreId: personSettings && personSettings.missingOrganizationUnitStoreId != null ? Number(personSettings.missingOrganizationUnitStoreId) : null,
          defaultCollectiveContractMdId: personSettings && personSettings.defaultCollectiveContractMdId != null ? personSettings.defaultCollectiveContractMdId : null
        };
        integration.savedPersonImportSettings = JSON.parse(JSON.stringify(integration.personImportSettings));
        integration.personImportSettingsLoaded = true;
      } catch (error) {
        integration.personImportSettingsError = 'Einstellungen für den Personenimport konnten nicht geladen werden.';
      } finally {
        integration.importSettingsLoading = false;
      }
    },
    importControlsDisabled(integration) {
      return integration.importSettingsSaving || integration.importSettingsLoading ||
        integration.refreshingOrganizationUnits || integration.refreshingPermanentEstablishments || integration.refreshingTimeRecordingRegulations;
    },
    defaultPersonImportSettings() {
      return {
        importPersonsWithoutTimeRecordingRegulation: false,
        importPersonsWithoutPermanentEstablishment: false,
        importPersonsWithoutOrganizationUnit: false,
        missingOrganizationUnitStoreId: null,
        defaultCollectiveContractMdId: null
      };
    },
    setMissingOrganizationUnitStore(integration, storeId) {
      if (this.importControlsDisabled(integration)) return;
      integration.personImportSettings.missingOrganizationUnitStoreId = storeId === '' ? null : Number(storeId);
      integration.personImportSettings.importPersonsWithoutOrganizationUnit = storeId !== '';
      this.savePersonImportSettings(integration);
    },
    setDefaultCollectiveContract(integration, contractId) {
      if (this.importControlsDisabled(integration)) return;
      const contract = this.collectiveContracts.find(item => String(item.id) === contractId);
      if (contractId !== '' && !contract) return;
      integration.personImportSettings.defaultCollectiveContractMdId = contractId === '' ? null : contract.id;
      this.savePersonImportSettings(integration);
    },
    async savePersonImportSettings(integration) {
      if (this.importControlsDisabled(integration)) return;
      integration.importSettingsSaving = true;
      integration.personImportSettingsError = '';
      integration.personImportSettingsNotice = '';
      const updatedSettings = JSON.parse(JSON.stringify(integration.personImportSettings));
      try {
        await this.importRequest('put', '/import-settings', integration, updatedSettings, true);
        integration.savedPersonImportSettings = updatedSettings;
        integration.personImportSettingsNotice = 'Einstellung gespeichert.';
      } catch (error) {
        integration.personImportSettings = JSON.parse(JSON.stringify(integration.savedPersonImportSettings));
        integration.personImportSettingsError = 'Einstellung konnte nicht gespeichert werden.';
      } finally {
        integration.importSettingsSaving = false;
      }
    },
    async refreshImportList(integration, kind) {
      const organizationUnits = kind === 'organization-units';
      const timeRecordingRegulations = kind === 'time-recording-regulations';
      const path = organizationUnits ? '/organization-units' : timeRecordingRegulations ? '/time-recording-regulations' : '/permanent-establishments';
      const busyKey = organizationUnits ? 'refreshingOrganizationUnits' : timeRecordingRegulations ? 'refreshingTimeRecordingRegulations' : 'refreshingPermanentEstablishments';
      if (this.importControlsDisabled(integration)) return;
      integration[busyKey] = true;
      integration.importSettingsError = '';
      try {
        await this.importRequest('post', path + '/refresh', integration);
        const result = await this.importRequest('get', path, integration);
        if (organizationUnits) integration.organizationUnits = Array.isArray(result) ? result : [];
        else if (timeRecordingRegulations) integration.timeRecordingRegulations = Array.isArray(result) ? result : [];
        else integration.permanentEstablishments = Array.isArray(result) ? result : [];
      } catch (error) {
        integration.importSettingsError = 'Die Liste konnte nicht aktualisiert werden.';
      } finally {
        integration[busyKey] = false;
      }
    },
    async mapOrganizationUnit(integration, unit, storeId) {
      if (this.importControlsDisabled(integration)) return;
      const id = this.importRecordId(unit, 'organization-units');
      if (id == null) {
        integration.importSettingsError = 'Die Organisationseinheit hat keine gültige ID.';
        return;
      }
      integration.importSettingsSaving = true;
      integration.importSettingsError = '';
      integration.importSettingsSaveError = '';
      try {
        const parsedStoreId = storeId === '' ? null : Number(storeId);
        // Axios omits null query values; send an empty value so the backend receives an explicit clear.
        const requestStoreId = parsedStoreId === null ? '' : parsedStoreId;
        await this.importRequest('put', '/organization-units/' + encodeURIComponent(id), integration, { storeId: requestStoreId });
        this.$set(unit, 'storeId', parsedStoreId);
      } catch (error) {
        integration.importSettingsSaveError = 'Die Store-Zuordnung konnte nicht gespeichert werden.';
      } finally {
        integration.importSettingsSaving = false;
      }
    },
    async setPermanentEstablishmentImport(integration, establishment, shouldImport) {
      if (this.importControlsDisabled(integration)) return;
      const id = this.importRecordId(establishment, 'permanent-establishments');
      if (id == null) {
        integration.importSettingsError = 'Die Niederlassung oder Betriebsstätte hat keine gültige ID.';
        return;
      }
      integration.importSettingsSaving = true;
      integration.importSettingsError = '';
      integration.importSettingsSaveError = '';
      try {
        await this.importRequest('put', '/permanent-establishments/' + encodeURIComponent(id), integration, { import: shouldImport ? 1 : 0 });
        this.$set(establishment, 'import', shouldImport);
      } catch (error) {
        integration.importSettingsSaveError = 'Die Import-Einstellung konnte nicht gespeichert werden.';
      } finally {
        integration.importSettingsSaving = false;
      }
    },
    async setTimeRecordingRegulationImport(integration, regulation, shouldImport) {
      if (this.importControlsDisabled(integration)) return;
      if (regulation.id == null) {
        integration.importSettingsError = 'Die Zeiterfassungsart hat keine gültige ID.';
        return;
      }
      integration.importSettingsSaving = true;
      integration.importSettingsError = '';
      integration.importSettingsSaveError = '';
      try {
        await this.importRequest('put', '/time-recording-regulations/' + encodeURIComponent(regulation.id), integration, { import: shouldImport ? 1 : 0 });
        this.$set(regulation, 'import', shouldImport);
      } catch (error) {
        integration.importSettingsSaveError = 'Die Import-Einstellung konnte nicht gespeichert werden.';
      } finally {
        integration.importSettingsSaving = false;
      }
    },
    defaultSettings() {
      return {
        persons: { onlyActive: false },
        absences: { onlyActive: false, statusFilter: [], types: [] },
        sickLeaves: { onlyActive: false, statusFilter: [], types: [] }
      };
    },
    defaultDatedImport() {
      const now = this.$helpers.getMomentTZ();
      return {
        beginDate: now.clone().startOf('month').format('YYYY-MM-DD'),
        endDate: now.clone().endOf('month').format('YYYY-MM-DD'),
        open: false, importing: false, notice: '', error: ''
      };
    },
    openDateRangeImport(integration, kind) {
      if (integration.busy || integration.settingsSaving) return;
      const range = kind === 'absences' ? integration.absenceImport : integration.sickLeaveImport;
      if (range.importing) return;
      range.notice = '';
      range.error = '';
      range.open = true;
    },
    closeDateRangeImport(range) {
      if (range.importing) return;
      range.open = false;
      range.error = '';
    },
    validImportDateRange(range) {
      return moment(range.beginDate, 'YYYY-MM-DD', true).isValid() &&
        moment(range.endDate, 'YYYY-MM-DD', true).isValid() && range.beginDate <= range.endDate;
    },
    updateImportDate(range, field, value) {
      range[field] = value;
      range.notice = '';
      range.error = '';
      if (!moment(range.beginDate, 'YYYY-MM-DD', true).isValid() || !moment(range.endDate, 'YYYY-MM-DD', true).isValid()) return;
      if (range.beginDate > range.endDate) {
        if (field === 'beginDate') range.endDate = moment(value, 'YYYY-MM-DD', true).endOf('month').format('YYYY-MM-DD');
        else range.beginDate = moment(value, 'YYYY-MM-DD', true).startOf('month').format('YYYY-MM-DD');
      }
    },
    async importDateRange(integration, kind) {
      const range = kind === 'absences' ? integration.absenceImport : integration.sickLeaveImport;
      if (!range.open || range.importing || integration.busy || integration.settingsSaving) return;
      range.notice = '';
      range.error = '';
      if (!this.validImportDateRange(range)) {
        range.error = 'Bitte einen gültigen Importzeitraum auswählen.';
        return;
      }
      range.importing = true;
      const label = kind === 'absences' ? 'Abwesenheitsimport' : 'Krankenstandsimport';
      try {
        await this.importRequest('post', '/' + kind + '/import', integration, { beginDate: range.beginDate, endDate: range.endDate }, true);
        range.notice = label + ' wurde gestartet.';
        range.open = false;
      } catch (error) {
        range.error = label + ' konnte nicht gestartet werden.';
      } finally {
        range.importing = false;
      }
    },
    async importPersons(integration) {
      if (integration.personsImporting || integration.busy || integration.settingsSaving || this.employeeSyncLoading || this.employeeSyncSaving) return;
      integration.personsImporting = true;
      integration.personsImportNotice = '';
      integration.personsImportError = '';
      try {
        await this.importRequest('post', '/persons/import', integration, {}, true);
        integration.personsImportNotice = 'Personenimport wurde gestartet.';
      } catch (error) {
        integration.personsImportError = 'Personenimport konnte nicht gestartet werden.';
      } finally {
        integration.personsImporting = false;
      }
    },
    async importRequest(method, path, integration, parameters = {}, sendBody = false) {
      const response = await axios({
        method,
        url: '/api/sec/hrworks' + path,
        headers: { AUTHORIZATION: 'Bearer ' + this.$store.state.jwt },
        ...(sendBody
          ? { data: { companyId: this.companyId, integrationId: integration.integrationId, ...parameters } }
          : { params: { companyId: this.companyId, integrationId: integration.integrationId, ...parameters } })
      });
      return this.unwrapResponse(response.data);
    },
    async typeMappingRequest(method, path, integration, data = null) {
      const parameters = { companyId: this.companyId, integrationId: integration.integrationId };
      const response = await axios({
        method,
        url: '/api/sec/hrworks' + path,
        headers: { AUTHORIZATION: 'Bearer ' + this.$store.state.jwt },
        ...(method === 'get' || method === 'delete'
          ? { params: { ...parameters, ...(data || {}) } }
          : { data: { ...parameters, ...(data || {}) } })
      });
      return this.unwrapResponse(response.data);
    },
    async hrworksRequest(method, path, integration, data = null, useQueryParams = false) {
      const parameters = { companyId: this.companyId, integrationId: integration.integrationId };
      const response = await axios({
        method, url: baseUrl + path,
        headers: { AUTHORIZATION: 'Bearer ' + this.$store.state.jwt },
        ...(method === 'get' || path.endsWith('/refresh') || useQueryParams
          ? { params: { ...parameters, ...(data || {}) } }
          : { data: { ...parameters, ...(data || {}) } })
      });
      return this.unwrapResponse(response.data);
    },
    async loadIntegrationSettings(integration) {
      integration.settingsLoading = true;
      integration.absenceTypesLoading = true;
      integration.sickLeaveTypesLoading = true;
      integration.settingsError = '';
      integration.absenceTypesError = '';
      integration.sickLeaveTypesError = '';
      const groups = [
        { key: 'absenceTypes', path: '/absence-types/stored' },
        { key: 'sickLeaveTypes', path: '/sick-leave-types/stored' }
      ];
      try {
        const [settingsResult, ...typeResults] = await Promise.all([
          this.hrworksRequest('get', '/integration-settings', integration),
          ...groups.map(group => this.hrworksRequest('get', group.path, integration)),
          this.typeMappingRequest('get', '/absence-type-mappings', integration),
          this.typeMappingRequest('get', '/sick-leave-type-mappings', integration)
        ]);
        const settings = settingsResult && typeof settingsResult === 'object' && !Array.isArray(settingsResult) ? settingsResult : {};
        integration.settingsEmpty = Object.keys(settings).length === 0;
        const defaults = this.defaultSettings();
        integration.settings = {
          persons: { ...defaults.persons, ...(settings.persons || {}) },
          absences: { ...defaults.absences, ...(settings.absences || {}), statusFilter: Array.isArray(settings.absences && settings.absences.statusFilter) ? [...settings.absences.statusFilter] : [], types: Array.isArray(settings.absences && settings.absences.types) ? [...settings.absences.types] : [] },
          sickLeaves: { ...defaults.sickLeaves, ...(settings.sickLeaves || {}), statusFilter: Array.isArray(settings.sickLeaves && settings.sickLeaves.statusFilter) ? [...settings.sickLeaves.statusFilter] : [], types: Array.isArray(settings.sickLeaves && settings.sickLeaves.types) ? [...settings.sickLeaves.types] : [] }
        };
        integration.savedSettings = JSON.parse(JSON.stringify(integration.settings));
        groups.forEach((group, index) => {
          const result = typeResults[index];
          integration[group.key] = Array.isArray(result) ? result.filter(type => type && type.key) : [];
        });
        integration.absenceTypeMappings = Array.isArray(typeResults[2]) ? typeResults[2] : [];
        integration.sickLeaveTypeMappings = Array.isArray(typeResults[3]) ? typeResults[3] : [];
        integration.settingsLoaded = true;
      } catch (error) {
        integration.settingsError = 'Integrationseinstellungen oder Typen konnten nicht geladen werden.';
      } finally {
        integration.settingsLoading = false;
        integration.absenceTypesLoading = false;
        integration.sickLeaveTypesLoading = false;
      }
    },
    allSettingsSelected(integration, group, field, values) {
      const selected = integration.settings[group][field];
      return values.length > 0 && values.every(value => selected.includes(value));
    },
    toggleAllSettings(integration, group, field, values) {
      if (integration.settingsSaving || !integration.settingsLoaded) return;
      if (field === 'types' && (!values.length || integration[group === 'sickLeaves' ? 'sickLeaveTypesLoading' : 'absenceTypesLoading'])) return;
      integration.settings[group][field] = this.allSettingsSelected(integration, group, field, values) ? [] : [...values];
    },
    toggleSettingValue(integration, group, field, value, checked) {
      if (integration.settingsSaving || !integration.settingsLoaded) return;
      const values = integration.settings[group][field];
      integration.settings[group][field] = checked
        ? [...new Set([...values, value])]
        : values.filter(item => item !== value);
    },
    typeMappingSelection(mappings, typeKey) {
      const mapping = (mappings || []).find(item => item.key === typeKey);
      if (!mapping || mapping.availabilityType == null) return '';
      return mapping.availabilitySubType == null
        ? 'type:' + mapping.availabilityType
        : 'subtype:' + mapping.availabilitySubType;
    },
    async upsertAvailabilityTypeMapping(integration, kind, type, value) {
      const sickLeave = kind === 'sick-leave';
      const mappingsKey = sickLeave ? 'sickLeaveTypeMappings' : 'absenceTypeMappings';
      const savingKey = sickLeave ? 'savingSickLeaveTypeMappings' : 'savingAbsenceTypeMappings';
      if (integration.settingsSaving || integration[savingKey]) return;
      const errorKey = sickLeave ? 'sickLeaveTypeMappingError' : 'absenceTypeMappingError';
      const endpoint = sickLeave ? '/sick-leave-type-mappings' : '/absence-type-mappings';
      const previousMappings = integration[mappingsKey];
      const existingIndex = previousMappings.findIndex(item => item.key === type.key);
      if (!value) {
        if (existingIndex === -1) return;
        integration[savingKey] = true;
        integration[errorKey] = '';
        try {
          await this.typeMappingRequest('delete', endpoint, integration, { key: type.key });
          integration[mappingsKey] = previousMappings.filter(item => item.key !== type.key);
        } catch (error) {
          integration[errorKey] = 'Zuordnung konnte nicht entfernt werden.';
        } finally {
          integration[savingKey] = false;
        }
        return;
      }
      const [selectionKind, selectedValue] = value.split(':');
      let availabilityType;
      let availabilitySubType = null;
      if (selectionKind === 'type') {
        availabilityType = Number(selectedValue);
      } else {
        const subType = (this.$helpers.GetMdEmployeeAvailabilitySubTypes() || []).find(item => Number(item.value) === Number(selectedValue));
        if (!subType) return;
        availabilityType = Number(subType.parentType);
        availabilitySubType = Number(subType.value);
      }
      const nextMapping = { key: type.key, name: type.name, availabilityType, availabilitySubType };
      integration[savingKey] = true;
      integration[errorKey] = '';
      try {
        await this.typeMappingRequest('put', endpoint, integration, nextMapping);
        const index = previousMappings.findIndex(item => item.key === type.key);
        integration[mappingsKey] = index === -1
          ? [...previousMappings, nextMapping]
          : previousMappings.map((item, itemIndex) => itemIndex === index ? nextMapping : item);
      } catch (error) {
        integration[errorKey] = 'Zuordnung konnte nicht gespeichert werden.';
      } finally {
        integration[savingKey] = false;
      }
    },
    settingsChanged(integration) {
      return integration.settingsLoaded && JSON.stringify(integration.settings) !== JSON.stringify(integration.savedSettings);
    },
    async saveIntegrationSettings(integration) {
      if (integration.settingsSaving || !this.settingsChanged(integration)) return;
      integration.settingsSaving = true;
      integration.settingsSaveError = '';
      integration.settingsSaveNotice = '';
      const updatedSettings = JSON.parse(JSON.stringify(integration.settings));
      try {
        await this.hrworksRequest('put', '/integration-settings', integration, { settings: updatedSettings });
        integration.settingsEmpty = false;
        integration.savedSettings = updatedSettings;
        integration.settingsSaveNotice = 'Integrationseinstellungen gespeichert.';
      } catch (error) {
        integration.settingsSaveError = 'Integrationseinstellungen konnten nicht gespeichert werden.';
      } finally {
        integration.settingsSaving = false;
      }
    },
    async refreshTypes(integration, kind) {
      const sickLeave = kind === 'sick-leave';
      const busyKey = sickLeave ? 'refreshingSickLeaveTypes' : 'refreshingAbsenceTypes';
      const listKey = sickLeave ? 'sickLeaveTypes' : 'absenceTypes';
      const storedPath = sickLeave ? '/sick-leave-types/stored' : '/absence-types/stored';
      const refreshPath = sickLeave ? '/sick-leave-types/refresh' : '/absence-types/refresh';
      const errorKey = sickLeave ? 'sickLeaveTypesError' : 'absenceTypesError';
      if (integration.settingsSaving || integration[busyKey]) return;
      integration[busyKey] = true;
      integration[errorKey] = '';
      try {
        await this.hrworksRequest('post', refreshPath, integration);
        const result = await this.hrworksRequest('get', storedPath, integration);
        integration[listKey] = Array.isArray(result) ? result.filter(type => type && type.key) : [];
      } catch (error) {
        integration[errorKey] = 'Typen konnten nicht aktualisiert werden.';
      } finally {
        integration[busyKey] = false;
      }
    },
    normalizeCredentialStatus(status) {
      return String(status || '').trim().toLowerCase();
    },
    visibleCredentials(integration) {
      return (integration.credentials || []).filter(credential => credential && !['retired', 'invalid'].includes(this.normalizeCredentialStatus(credential.status)));
    },
    hasRetiringCredentials(integration) {
      return Object.entries(integration.credentialStatusCounts || {}).some(([status, count]) =>
        this.normalizeCredentialStatus(status) === 'retiring' && Number(count) > 0) ||
        this.visibleCredentials(integration).some(credential => this.normalizeCredentialStatus(credential.status) === 'retiring');
    },
    async refreshCredentialMetadata(integration) {
      integration.busy = true;
      const generation = this.generation;
      try {
        const result = await this.request('get', '/integrations');
        if (generation !== this.generation) return;
        const list = Array.isArray(result) ? result : result && result.integrations;
        const updated = Array.isArray(list) && list.find(item => item.integrationId === integration.integrationId);
        if (!updated) throw new Error('Integration missing');
        integration.credentials = Array.isArray(updated.credentials) ? updated.credentials : [];
        integration.credentialStatusCounts = updated.credentialStatusCounts || {};
      } catch (error) {
        if (generation === this.generation) integration.error = 'Zugangsschlüssel konnten nicht aktualisiert werden. Bitte Integrationen neu laden.';
      } finally {
        if (generation === this.generation) integration.busy = false;
      }
    },
    async authenticate(integration) {
      if (integration.active === false || integration.busy) return;
      const generation = this.generation;
      integration.busy = true;
      integration.error = '';
      integration.authStatus = 'authenticating';
      try {
        const result = await this.request('post', '/integrations/authenticate', { integrationId: integration.integrationId });
        if (generation !== this.generation) return;
        if (result === false || (result && (result.authenticated === false || result.authenticationSuccessful === false))) {
          throw new Error('Authentication failed');
        }
        integration.authStatus = 'authenticated';
      } catch (error) {
        if (generation !== this.generation) return;
        integration.authStatus = 'failed';
        integration.error = 'Authentifizierung fehlgeschlagen. Zugangsdaten prüfen und erneut versuchen.';
      } finally {
        // Authentication can validate or invalidate credentials on the backend.
        if (generation === this.generation) await this.refreshCredentialMetadata(integration);
      }
    },
    openCreate() {
      this.newCredentials = { integrationId: window.crypto.randomUUID(), accessKey: '', secretAccessKey: '' };
      this.showCreate = true;
    },
    cancelCreate() {
      this.showCreate = false;
      this.newCredentials = { integrationId: '', accessKey: '', secretAccessKey: '' };
    },
    cancelRotation(integration) {
      integration.showCredentials = false;
      integration.accessKey = '';
      integration.secretAccessKey = '';
    },
    async saveCredentials(integration = null) {
      if (integration ? integration.busy : this.busy) return;
      const credentials = integration || this.newCredentials;
      if (!credentials.integrationId || !credentials.accessKey || !credentials.secretAccessKey) return;
      const generation = this.generation;
      if (integration) { integration.busy = true; integration.error = ''; }
      else { this.creating = true; this.error = ''; }
      this.notice = '';
      try {
        await this.request('post', integration ? '/integrations/credentials/rotate' : '/integrations', {
          integrationId: credentials.integrationId, accessKey: credentials.accessKey, secretAccessKey: credentials.secretAccessKey
        });
        if (generation !== this.generation) return;
        if (integration) {
          this.cancelRotation(integration);
          integration.busy = false;
          this.notice = 'Zugangsschlüssel rotiert.';
          await this.authenticate(integration);
        } else {
          const integrationId = credentials.integrationId;
          this.cancelCreate();
          await this.loadIntegrations(integrationId);
        }
      } catch (error) {
        if (generation !== this.generation) return;
        if (integration) integration.error = 'Zugangsschlüssel konnten nicht rotiert werden.';
        else this.error = 'Integration konnte nicht angelegt werden.';
      } finally {
        // Clear entered secrets even when the request fails.
        credentials.accessKey = '';
        credentials.secretAccessKey = '';
        if (integration) integration.busy = false;
        else if (generation === this.generation || !this.showCreate) this.creating = false;
      }
    },
    async changeIntegration(integration, action) {
      if (integration.busy || !window.confirm(action.confirm)) return;
      const generation = this.generation;
      integration.busy = true;
      integration.error = '';
      this.notice = '';
      try {
        await this.request('put', action.path, { integrationId: integration.integrationId });
        if (generation !== this.generation) return;
        this.notice = action.label + ': erfolgreich.';
        if (action.authenticate === false) {
          integration.active = false;
          if (this.webhookIntegrationId === integration.integrationId) this.webhookIntegrationId = '';
          integration.authStatus = 'unauthenticated';
        }
        if (action.authenticate === true) {
          integration.busy = false;
          await this.authenticate(integration);
        }
        if (action.authenticate !== true && action.path.startsWith('/integrations/credentials/')) await this.refreshCredentialMetadata(integration);
      } catch (error) {
        if (generation === this.generation) integration.error = action.label + ': fehlgeschlagen.';
      } finally {
        if (generation === this.generation) integration.busy = false;
      }
    },
    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('de-AT');
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '—';
      if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
      if (Array.isArray(value)) return value.length ? value.map(this.displayValue).join(', ') : '—';
      if (typeof value === 'object') return Object.entries(value).map(([key, item]) => key + ': ' + this.displayValue(item)).join('\n');
      return String(value);
    },
    async loadWebhooks(kind, id = '', integrationId = this.webhookIntegrationId) {
      if (this.webhookLoading) return;
      const generation = this.generation;
      const filter = integrationId;
      this.webhookLoading = true;
      this.webhookError = '';
      this.webhookResult = null;
      try {
        const result = await this.request('get', '/webhooks/inbox/' + kind + (id ? '/' + encodeURIComponent(id) : ''),
          filter ? { integrationId: filter } : {});
        if (generation !== this.generation) return;
        this.webhookResult = result;
        this.webhookResultKind = kind === 'status' ? 'status' : id ? 'dead-letter' : 'dead-letters';
        this.webhookResultLabel = (kind === 'status' ? 'Status' : 'Dead Letters') + (id ? ' · ' + id : '') + ' · ' + (filter || 'Alle Integrationen');
      } catch (error) {
        if (generation === this.generation) this.webhookError = 'Webhook Daten konnten nicht geladen werden.';
      } finally {
        if (generation === this.generation) this.webhookLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.hrworks { width: 98%; margin: 0 1%; clear: both; padding-bottom: 20px; text-align: left; font-size: 13px; }
.header { color: #e51550; font-size: 15px; font-weight: 700; border-bottom: 1px solid #ccc; padding: 7px; }
.card { border: 1px solid #ccc; padding: 14px; margin-top: 12px; }
.toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
label { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 10px 0; }
input, select { max-width: 100%; width: 300px; }
.hrworks .hrworks-button.button.rectangle { width: auto; min-width: max-content; flex: 0 0 auto; white-space: nowrap; min-height: 32px; margin: 0 6px 0 0; padding: 0 12px; border-radius: 4px; }
.error { color: #c52340; }
.status-heading { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin: 16px 0; }
.health-badge { display: inline-block; padding: 6px 10px; border-radius: 4px; font-weight: 700; }
.healthy { color: #24613a; background: #e8f5ec; }
.unhealthy { color: #a51d35; background: #fdecef; }
.metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: 10px; }
.metric { padding: 12px; border: 1px solid #ccc; border-radius: 4px; }
.metric span { display: block; min-height: 32px; }
.metric strong { display: block; font-size: 24px; }
.positive strong { color: #24613a; }
.warning strong { color: #8a5900; }
.negative strong { color: #a51d35; }
.detail-grid { display: grid; grid-template-columns: minmax(150px, 1fr) minmax(0, 2fr); margin: 16px 0; }
.detail-grid dt, .detail-grid dd { padding: 8px; margin: 0; border-bottom: 1px solid #ddd; }
.detail-grid dd { overflow-wrap: anywhere; white-space: pre-wrap; }
.table-container { overflow-x: auto; }
.dead-letter-table { width: 100%; border-collapse: collapse; text-align: left; }
.dead-letter-table caption { text-align: left; padding: 8px 0; }
.dead-letter-table th, .dead-letter-table td { padding: 10px; border-bottom: 1px solid #ddd; vertical-align: top; }
.dead-letter-table th { background: #f5f5f5; }
.dead-letter-table td { min-width: 100px; max-width: 260px; overflow-wrap: anywhere; }
.secondary { display: block; color: #666; font-size: 12px; margin-top: 6px; }
.credential-list { margin-top: 16px; }
.integration-settings { margin: 18px 0; padding: 14px; border: 1px solid #ddd; border-radius: 4px; }
.integration-settings h4 { margin: 0; }
.settings-panel-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; }
.settings-toggle { margin-bottom: 0; }
.settings-layout { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; align-items: start; }
.import-layout { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; align-items: stretch; width: 100%; }
.settings-group { min-width: 0; margin: 14px 0; padding: 12px; background: #f8f8f8; }
.settings-group h5 { margin: 4px 0 10px; }
.import-row { justify-content: space-between; gap: 12px; border-bottom: 1px solid #e1e1e1; padding: 8px 0; }
.import-row > span:first-child { flex: 1; overflow-wrap: anywhere; }
.import-row select { width: min(220px, 45%); }
.import-row input[type="checkbox"] { width: auto; }
.employee-sync-option { justify-content: space-between; flex-wrap: nowrap; align-items: center; }
.employee-sync-option > span { flex: 1; min-width: 0; }
.employee-sync-option select { width: min(340px, 55%); }
.employee-sync-option input[type="checkbox"] { width: auto; }
.default-contract-setting { display: flex; align-items: center; }
.default-contract-setting select { width: min(420px, 60%); }
.settings-group > strong { display: block; margin: 8px 0; }
.settings-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 8px 0; }
.settings-heading > strong { margin: 0; }
.dated-import-panel { margin: 12px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #fff; }
.import-date-range { display: flex; flex-wrap: wrap; gap: 12px; }
.import-date-range label { flex: 1 1 160px; min-width: 0; }
.import-date-range input { width: 100%; min-width: 0; }
.inline-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 4px; }
.settings-heading .hrworks-button.button.rectangle { min-height: 24px; height: 24px; padding: 0 7px; margin: 0; font-size: 11px; }
.setting-option { display: flex; width: 100%; margin: 8px 0; align-items: flex-start; }
.setting-option input { width: auto; }
.type-mapping-option { align-items: center; gap: 10px; }
.type-option-name { flex: 1; min-width: 120px; }
.availability-type-select { width: min(360px, 42%); }
.availability-type-select.availability-type-unmapped { background-color: #fff3cd; border: 1px solid #e0a800; color: #765600; font-weight: 600; }
.credential-warning { color: #805200; background: #fff4d6; border: 1px solid #e4c575; padding: 12px; border-radius: 4px; }
.empty-state { padding: 16px; background: #f5f5f5; }
@media (max-width: 900px) {
  .settings-layout, .import-layout { grid-template-columns: 1fr; gap: 0; }
}
@media (max-width: 600px) {
  .detail-grid { grid-template-columns: 1fr; }
  .detail-grid dt { border-bottom: 0; padding-bottom: 0; }
}
</style>
